import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IServerSideGetRowsParams } from 'ag-grid-community';
import { ColDef, ColGroupDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { BehaviorSubject, of } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { takeUntil, tap } from 'rxjs/operators';

import {
  ApplicationSettingService,
  BaseEditService,
  BaseImportService,
  FormlyService,
  MenuService,
  ModalService,
  RouterService,
  UserPreferenceService,
  UtilityService,
} from '../../../services';
import {
  FormlyButtonDropdown,
  GridRowSelect,
  GridState,
  IdDescription,
  Search,
  SearchPage,
  UserPreference,
} from '../../../models';
import { MockEditService } from '../../../mock';
import { GridComponent } from '../grid/grid.component';
import { BaseComponent } from '../../base/base.component';
import { BaseInlineFormComponent } from '../../base-inline-form/base-inline-form.component';
import { FormOkCancelModalComponent, OkCancelModalComponent } from '../../modals';

@Component({
  selector: 'app-search-grid',
  templateUrl: './search-grid.component.html',
  styleUrl: './search-grid.component.scss',
})
export class SearchGridComponent extends BaseComponent implements OnInit {
  @ViewChild(BaseInlineFormComponent, { static: true }) form: BaseInlineFormComponent;
  @ViewChild(GridComponent, { static: true }) grid: GridComponent;

  @Input() stateKey: string;
  @Input() header: string;
  @Input() dropDownText = $localize`Download Template`;
  @Input() service: BaseEditService<any, any> = new MockEditService();
  @Input() importService: BaseImportService;
  @Input() importButtonText = $localize`Import`;
  @Input() showCreate = true;
  @Input() showDelete = true;
  @Input() showDeleteMessage = true;
  @Input() showBack = false;
  @Input() showExport = true;
  @Input() showPrint = false;
  @Input() showPrintTop = false;
  @Input() showLayouts = false;
  @Input() showReport = false;
  @Input() showReport2 = false;
  @Input() reportButtonText = $localize`Report`;
  @Input() report2ButtonText: string;
  @Input() showIncludeAll = false;
  @Input() includeAllText = $localize`Include All`;
  @Input() allowIncludeAllBlankSearch = false;
  @Input() blankSearchSetting: string;
  @Input() allowNavigateOnSingleResult = true;
  @Input() allowEdit = true;
  @Input() gridCssClass: string;
  @Input() columnDefs?: (ColDef | ColGroupDef)[] = [];
  @Input() initialSearch = true;
  @Input() allowInitialBlankSearch = true;
  @Input() advancedSearchTooltip = false;
  @Input() filter = true;
  @Input() resizable = true;
  @Input() sortable = true;
  @Input() fields: FormlyFieldConfig[] = [];
  @Input() additionalFields: FormlyFieldConfig[] = [];
  @Input() additionalFieldsRight: FormlyFieldConfig[] = [];
  @Input() id: string = undefined;
  @Input() id2: string = undefined;
  @Input() createDropdown: FormlyButtonDropdown;
  @Input() importDropdown: FormlyButtonDropdown;
  @Input() printDropdown: FormlyButtonDropdown;
  @Input() saveSearch = true;
  @Input() serverSide = false;
  @Input() pageSize = 100;
  @Output() create = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() print = new EventEmitter<any>();
  @Output() printTop = new EventEmitter<any>();
  @Output() rowSelected = new EventEmitter<GridRowSelect>();

  rowData$: Observable<any> = of([]);
  model: Search = {} as Search;
  private allowBlankSearch = false;

  private layoutFields: FormlyFieldConfig[];
  private layouts$ = new BehaviorSubject<UserPreference[]>([]);
  private layoutsSelectOptions$ = new BehaviorSubject<IdDescription[]>([]);

  protected readyForSearch = false;
  private serverSideParams: IServerSideGetRowsParams;

  constructor(
    private formlyService: FormlyService,
    private routerService: RouterService,
    private route: ActivatedRoute,
    private applicationSettingService: ApplicationSettingService,
    private userPreferenceService: UserPreferenceService,
    private menuService: MenuService,
    private modalService: ModalService,
    private utilityService: UtilityService,
  ) {
    super();
  }

  ngOnInit() {
    this.checkForBlankSearch();
    if (this.showLayouts) {
      this.getLayouts().subscribe();
      this.getLayoutFields();
    }
  }

  refresh() {
    this.grid.loadState();
    this.onSearch();
  }

  updateForm() {
    this.form.update();
  }

  protected onSearch(navigateOnSingleResult = false) {
    if (this.serverSide) {
      this.grid.gridApi.refreshServerSide({ purge: navigateOnSingleResult });
    } else {
      this.setResults(
        this.service.search(
          this.model.search,
          this.id ?? this.model['id'],
          this.id2 ?? this.model['id2'],
          this.model.includeAll,
          this.allowIncludeAllBlankSearch,
          this.allowBlankSearch,
        ),
        navigateOnSingleResult,
      );
    }
  }

  protected serverSideSearch(params: IServerSideGetRowsParams, navigateOnSingleResult = false) {
    if (!this.readyForSearch) {
      this.serverSideParams = params;
      return;
    }

    this.service
      .searchPage(
        this.model.search,
        this.id ?? this.model['id'],
        this.id2 ?? this.model['id2'],
        this.model.includeAll,
        this.allowIncludeAllBlankSearch,
        this.allowBlankSearch,
        params.request,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchPage: SearchPage<any>) => {
        this.saveSearchModel();
        params.success({
          rowData: searchPage.results,
          rowCount: searchPage.count,
        });
        if (this.model.search && this.allowNavigateOnSingleResult && navigateOnSingleResult && searchPage.count === 1) {
          this.onEdit(searchPage.results[0]);
        }
      });
  }

  protected onCreate() {
    if (this.create.observed) {
      this.create.emit();
    } else {
      this.routerService.create(this.route);
    }
  }

  protected onEdit(model: any) {
    if (this.edit.observed) {
      this.edit.emit(model.id);
    } else {
      if (this.allowEdit) {
        this.routerService.edit(model.id, this.route);
      }
    }
  }

  protected onDelete(model: any) {
    if (this.delete.observed) {
      this.delete.emit(model);
    } else {
      this.service
        .delete(model.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.onSearch());
    }
  }

  protected imported() {
    this.onSearch();
  }

  protected report() {
    this.routerService.report(this.route);
  }

  protected report2() {
    this.routerService.report2(this.route);
  }

  protected get gridStateKey() {
    return `Search.${this.stateKey ?? this.header}`;
  }

  protected get layoutStateKey() {
    return this.layouts$.value.find((userPreference: UserPreference) => userPreference.id === this.model.layout)?.key;
  }

  private get layoutUserPreferenceKey() {
    return `Layout.Search.${this.stateKey ?? this.header}.`;
  }

  private get layoutUserPreferenceKeyGlobal() {
    return `${this.layoutUserPreferenceKey}Global.`;
  }

  private checkForBlankSearch() {
    if (this.blankSearchSetting) {
      this.applicationSettingService
        .getBoolean(this.blankSearchSetting)
        .pipe(takeUntil(this.destroy$))
        .subscribe((allowBlankSearch: boolean) => {
          this.allowBlankSearch = allowBlankSearch;
          this.getFields();
          this.performInitialSearch();
        });
    } else {
      this.allowBlankSearch = true;
      this.getFields();
      this.performInitialSearch();
    }
  }

  private performInitialSearch() {
    this.userPreferenceService
      .get(this.gridStateKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userPreference: UserPreference) => {
        if (userPreference.value?.startsWith('{')) {
          this.model = JSON.parse(userPreference.value);
        } else {
          this.model.search = userPreference.value;
        }
        this.readyForSearch = true;
        if (
          this.initialSearch &&
          (this.model.search || this.model.id || this.model.id2 || this.allowInitialBlankSearch)
        ) {
          if (this.serverSide && this.serverSideParams) {
            this.serverSideSearch(this.serverSideParams);
          } else {
            this.onSearch();
          }
        } else if (this.serverSide && this.serverSideParams) {
          this.serverSideParams.success({
            rowData: [],
            rowCount: 0,
          });
        }
      });
  }

  private getFields() {
    const fields: FormlyFieldConfig[] = [];

    if (this.showBack) {
      fields.push(this.formlyService.backButton(this.route));
    }

    fields.push(
      this.formlyService.search(
        'search',
        '',
        this.service,
        (results: Observable<any[]>) => this.setResults(results, true),
        {
          id: this.id,
          id2: this.id2,
          allowIncludeAllBlankSearch: this.allowIncludeAllBlankSearch,
          allowBlankSearch: this.allowBlankSearch,
          advancedSearchTooltip: this.advancedSearchTooltip,
          search: () => this.onSearch(true),
        },
      ),
    );

    if (this.showCreate && !this.menuService.isReadOnly) {
      fields.push(this.formlyService.createButton(() => this.onCreate(), { dropdown: this.createDropdown }));
    }

    if (this.showExport) {
      fields.push(this.formlyService.exportButton(this.grid, this.header));
    }

    if (this.importService && !this.menuService.isReadOnly) {
      if (this.importDropdown) {
        fields.push(
          this.formlyService.importButton(this.importButtonText, (file: any) => this.grid.import(file), {
            dropdown: this.importDropdown,
          }),
        );
      } else {
        fields.push(
          this.formlyService.fileUpload(
            this.importButtonText,
            (file: any) => this.grid.import(file),
            () => this.grid.exportFile(),
            { dropDownText: this.dropDownText },
          ),
        );
      }
    }

    if (this.showPrint) {
      fields.push(this.formlyService.printButton(() => this.printTop.emit(), { dropdown: this.printDropdown }));
    }

    if (this.showReport) {
      fields.push(
        this.formlyService.button(this.reportButtonText, () => this.report(), {
          btnClass: 'secondary',
          iconClass: 'fa fa-file-invoice',
        }),
      );
    }

    if (this.showReport2) {
      fields.push(
        this.formlyService.button(this.report2ButtonText, () => this.report2(), {
          btnClass: 'secondary',
          iconClass: 'fa fa-file-invoice',
        }),
      );
    }

    if (this.additionalFields) {
      fields.push(...this.additionalFields);
    }

    if (this.showIncludeAll) {
      fields.push(
        this.formlyService.checkbox('includeAll', this.includeAllText, undefined, {
          onChange: () => this.onSearch(),
        }),
      );
    }

    if (this.showLayouts) {
      fields.push(
        ...[
          this.formlyService.select('layout', $localize`Layout`, this.layoutsSelectOptions$, undefined, {
            change: (field: FormlyFieldConfig) => this.changeLayout(field),
            create: () => this.createLayout(),
            delete: () => this.deleteLayout(),
            showDelete: () => this.canDeleteLayout(),
          }),
        ],
      );
    }

    if (this.additionalFieldsRight) {
      fields.push(...this.additionalFieldsRight);
    }

    this.fields = fields;
  }

  private setResults(results: Observable<any[]>, navigateOnSingleResult = false) {
    this.saveSearchModel();

    if (results) {
      this.rowData$ = results.pipe(
        takeUntil(this.destroy$),
        tap((rowData: any[]) => {
          if (
            this.model.search &&
            this.allowNavigateOnSingleResult &&
            navigateOnSingleResult &&
            rowData?.length === 1
          ) {
            this.onEdit(rowData[0]);
          }
        }),
      );
    }
  }

  private canDeleteLayout() {
    return (
      this.layouts$.value.find((userPreference: UserPreference) => userPreference.id === this.model.layout)
        ?.canDelete ?? false
    );
  }

  private getLayoutFields() {
    this.layoutFields = [
      this.formlyService.text('name', $localize`Layout Name`, undefined, { required: true, maxLength: 100 }),
      this.formlyService.checkbox('global', $localize`Global to all users`),
    ];
  }

  protected getLayouts(): Observable<UserPreference[]> {
    return this.userPreferenceService.search(this.layoutUserPreferenceKey).pipe(
      takeUntil(this.destroy$),
      tap((userPreferences: UserPreference[]) => {
        this.layouts$.next(userPreferences);
        this.layoutsSelectOptions$.next(
          this.utilityService.sort(
            userPreferences.map(
              (userPreference: UserPreference): IdDescription => ({
                id: userPreference.id,
                description: userPreference.key.includes(this.layoutUserPreferenceKeyGlobal)
                  ? `${userPreference.key.replace(this.layoutUserPreferenceKeyGlobal, '')} (${$localize`Global`})`
                  : userPreference.key.replace(this.layoutUserPreferenceKey, ''),
              }),
            ),
            (option: IdDescription) => option.description,
          ),
        );

        if (
          this.model.layout &&
          userPreferences.every((userPreference: UserPreference) => userPreference.id !== this.model.layout)
        ) {
          this.selectDefaultLayout();
        }
      }),
    );
  }

  private createLayout() {
    const { modal, component } = this.modalService.openMedium<FormOkCancelModalComponent>(FormOkCancelModalComponent);
    component.header = $localize`New Layout`;
    component.fields = this.layoutFields;
    modal.result.then(
      (model) => {
        const key = `${model.global ? this.layoutUserPreferenceKeyGlobal : this.layoutUserPreferenceKey}${model.name}`;
        this.userPreferenceService.set(key, JSON.stringify(this.grid.state)).subscribe(() => {
          this.getLayouts().subscribe((layouts: UserPreference[]) => {
            const layout = layouts.find((x) => x.key === key);
            this.model.layout = layout.id;
            this.form.updateModel({ layout: layout.id });
            this.saveSearchModel();
          });
        });
      },
      () => {},
    );
  }

  private deleteLayout() {
    const { modal, component } = this.modalService.openMedium<OkCancelModalComponent>(OkCancelModalComponent);
    component.header = $localize`Delete Layout`;
    component.message = $localize`Are you sure you want to delete this layout?`;
    modal.result.then(
      () =>
        this.userPreferenceService
          .delete(this.model.layout)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.selectDefaultLayout()),
      () => {},
    );
  }

  private changeLayout(field: FormlyFieldConfig) {
    if (field.model.layout) {
      const layout = this.layouts$.value.find((x) => x.id === field.model.layout);
      this.grid.state = JSON.parse(layout.value) as GridState;
      this.grid.applyState();
    } else {
      setTimeout(() => this.grid.loadState(), 0);
    }
    this.saveSearchModel();
  }

  private selectDefaultLayout() {
    delete this.model.layout;
    this.form.updateModel({ layout: undefined });
    this.saveSearchModel();
    this.getLayouts().subscribe(() => setTimeout(() => this.grid.loadState(), 0));
  }

  private saveSearchModel() {
    if (this.saveSearch) {
      this.userPreferenceService.set(this.gridStateKey, JSON.stringify(this.model)).subscribe();
    }
  }

  protected savedState() {
    this.getLayouts().subscribe();
  }
}
